import React from "react";
import { money, num } from "@/lib/format";
import { ShowMath } from "@/components/ShowMath";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Download, FileSpreadsheet } from "lucide-react";
import { COST_COLORS, COST_LEGEND } from "@/lib/costColors";
import { CostWaterfall, CostDonut } from "@/components/charts/ChartKit";
import { useDensity } from "@/lib/useDensity";
import { DensityToggle } from "@/components/ui/DensityToggle";
import ScopeGapCard from "@/components/ScopeGapCard";
import BenchmarkAlertCard from "@/components/BenchmarkAlertCard";
import SmartSuggestionsCard from "@/components/SmartSuggestionsCard";
import CommodityExposureCard from "@/components/CommodityExposureCard";
import api from "@/lib/api";
import { toast } from "sonner";

function costCell(line, key, label, testid) {
  if (line.status !== "priced") {
    return <span className="text-amber-500 font-mono text-xs" title="Missing input">missing</span>;
  }
  const d = line.derivation || {};
  const rowMap = {
    labor_cost: [{ title: "On-site days", text: d.on_site_days }, { title: "Labor", text: d.labor }, ...(d.labor_rows || []).map((r) => ({ text: `${r.role}: ${r.formula}` }))],
    equipment_cost: [{ title: "On-site days", text: d.on_site_days }, { title: "Equipment", text: d.equipment }, ...(d.equipment_rows || []).map((r) => ({ text: r.formula }))],
    per_diem_cost: [{ title: "Per-diem", text: d.per_diem }],
    material_cost: [{ title: "Material", text: d.material }],
    subtotal: [
      { title: "Crew-days", text: d.crew_days }, { title: "Crews", text: d.crews },
      { title: "On-site", text: d.on_site_days }, { title: "Labor", text: d.labor },
      { title: "Equipment", text: d.equipment }, { title: "Per-diem", text: d.per_diem },
      { title: "Material", text: d.material },
    ],
  };
  return (
    <ShowMath
      testid={testid}
      label={`${line.name} — ${label}`}
      value={money(line[key], key === "subtotal" ? 0 : 0)}
      rows={rowMap[key] || []}
      summary={key === "subtotal" ? d.subtotal : undefined}
      tone={key === "subtotal" ? "text-slate-900 font-semibold" : "text-slate-700"}
    />
  );
}

export default function PricingMath({ bid, computed, updateBid, perms, readOnly, termsLocked }) {
  const t = computed.totals;
  const C = COST_COLORS;
  const priced = (computed.lines || []).filter((l) => l.enabled !== false);
  // Lines in scope order, each scope followed by the costs the crew carries (equipment per machine, per-diem), then one line that foots to the direct work.
  const rollupRows = (() => {
    const order = [];
    priced.forEach((l) => { const g = l.group || "__none__"; if (!order.includes(g)) order.push(g); });
    const out = [];
    const tot = { labor: 0, equipment: 0, per_diem: 0, material: 0, subtotal: 0 };
    order.forEach((g) => {
      priced.filter((l) => (l.group || "__none__") === g).forEach((l) => {
        out.push({ kind: "line", key: l.line_id, line: l });
        tot.labor += Number(l.labor_cost) || 0; tot.equipment += Number(l.equipment_cost) || 0; tot.per_diem += Number(l.per_diem_cost) || 0; tot.material += Number(l.material_cost) || 0; tot.subtotal += Number(l.subtotal) || 0;
      });
      const e = (computed.groups || {})[g];
      (e?.equipment_rows || []).filter((r) => Number(r.pretax) > 0).forEach((r, i) => {
        out.push({ kind: "scope", key: `${g}-eq-${i}`, testid: `rollup-scope-equip-${g}-${i}`, name: `${r.name}`, sub: `${g} crew equipment · ${num(r.qty)} × ${r.ownership}${r.site_days != null ? ` · ${num(r.site_days)} site-days` : ""}`,
          equipment: Number(r.pretax) || 0, per_diem: 0, subtotal: Number(r.pretax) || 0, title: r.derivation?.line || "", rows: [{ title: "Equipment", text: r.derivation?.line || "" }] });
        tot.equipment += Number(r.pretax) || 0; tot.subtotal += Number(r.pretax) || 0;
      });
      if (Number(e?.per_diem_cost) > 0) {
        out.push({ kind: "scope", key: `${g}-pd`, testid: `rollup-scope-perdiem-${g}`, name: "Crew per-diem", sub: `${g} crew · ${e.work_days != null ? `${num(e.work_days)} work-days` : ""}`,
          equipment: 0, per_diem: Number(e.per_diem_cost), subtotal: Number(e.per_diem_cost), title: e.per_diem_detail || "", rows: [{ title: "Per-diem", text: e.per_diem_detail || "" }] });
        tot.per_diem += Number(e.per_diem_cost); tot.subtotal += Number(e.per_diem_cost);
      }
    });
    if (out.length) out.push({ kind: "total", key: "__total__", testid: "rollup-scope-total", name: "Direct work (scopes, crew equipment, crew per-diem)", ...tot });
    return out;
  })();
  const lockInputs = readOnly || termsLocked;
  const { compact, toggle: toggleDensity, cellPad } = useDensity();
  const dl = async (fmt, group) => {
    try {
      const res = await api.get(`/bids/${bid.bid_id}/itemized-estimate`, { params: { fmt, group }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `itemized_estimate_${group === "cost_code" ? "by_cost_code" : "by_department"}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("itemized export failed", e); toast.error("Could not export the estimate"); }
  };

  // Imported / read-only records carry a stored $ from the old system and have no line
  // breakdown — show a clean stored summary instead of an empty $0 roll-up.
  if (bid.read_only) {
    return (
      <div className="p-6" data-testid="rollup-imported-summary">
        <div className="max-w-[1500px]">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Imported record — stored summary</h2>
          <Card className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Total (as priced)</div><div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1">{money(bid.stored_total)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">$/Watt</div><div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1">{bid.stored_cost_per_watt ? `$${Number(bid.stored_cost_per_watt).toFixed(4)}` : "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Capacity</div><div className="text-2xl font-mono font-bold text-slate-900 tabular-nums mt-1">{bid.capacity_mw ? `${num(bid.capacity_mw)} MW` : "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Outcome</div><div className="text-2xl font-semibold text-slate-900 mt-1 capitalize">{bid.outcome || bid.status || "—"}</div></div>
            </div>
            <p className="text-xs text-slate-500 mt-5 max-w-2xl">Imported from your previous system and stored exactly as priced/sent. It powers your <b>benchmarks</b> and <b>pricing history</b>, but has no line-level breakdown to roll up here.{bid.loss_reason ? ` Loss reason: ${bid.loss_reason}.` : ""}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <BenchmarkAlertCard bidId={bid.bid_id} />
        <CommodityExposureCard bidId={bid.bid_id} />
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Priced scope — every number shows its math</h2>
          <DensityToggle compact={compact} onToggle={toggleDensity} testid="rollup-density-toggle" />
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                <th className="text-left font-medium px-3 py-2">Scope</th>
                <th className="text-right font-medium px-3 py-2">Labor</th>
                <th className="text-right font-medium px-3 py-2">Equipment</th>
                <th className="text-right font-medium px-3 py-2">Per-diem</th>
                <th className="text-right font-medium px-3 py-2">Material</th>
                <th className="text-right font-medium px-3 py-2">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {rollupRows.map((r) => r.kind === "scope" ? (
                <tr key={r.key} className="border-b border-slate-50 bg-amber-50/40" data-testid={r.testid} title={r.title}>
                  <td className={`px-3 ${cellPad}`}>
                    <div className="font-medium text-slate-800">{r.name}</div>
                    <div className="text-[11px] text-slate-400">{r.sub}</div>
                  </td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-slate-300`}>—</td>
                  <td className={`px-3 ${cellPad} text-right`}>{r.equipment ? <ShowMath testid={`${r.testid}-equip`} label={`${r.name} — Equipment`} value={money(r.equipment)} rows={r.rows} /> : <span className="font-mono text-slate-300">—</span>}</td>
                  <td className={`px-3 ${cellPad} text-right`}>{r.per_diem ? <ShowMath testid={`${r.testid}-perdiem`} label={`${r.name} — Per-diem`} value={money(r.per_diem)} rows={r.rows} /> : <span className="font-mono text-slate-300">—</span>}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-slate-300`}>—</td>
                  <td className={`px-3 ${cellPad} text-right font-mono font-semibold text-slate-900`}>{money(r.subtotal)}</td>
                </tr>
              ) : r.kind === "total" ? (
                <tr key={r.key} className="border-b border-slate-100 bg-slate-50" data-testid={r.testid}>
                  <td className={`px-3 ${cellPad} text-xs font-semibold text-slate-600`}>{r.name}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-xs text-slate-600`}>{money(r.labor)}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-xs text-slate-600`}>{money(r.equipment)}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-xs text-slate-600`}>{money(r.per_diem)}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-xs text-slate-600`}>{money(r.material)}</td>
                  <td className={`px-3 ${cellPad} text-right font-mono text-sm font-semibold text-slate-900`}>{money(r.subtotal)}</td>
                </tr>
              ) : (() => { const l = r.line; return (
                <tr key={l.line_id} className="border-b border-slate-50" data-testid={`pricing-row-${l.line_id}`}>
                  <td className={`px-3 ${cellPad}`}>
                    <div className="font-medium text-slate-800">{l.name}</div>
                    <div className="text-[11px] text-slate-400">{num(l.quantity)} {l.unit} · <span className="capitalize">{l.discipline}</span></div>
                  </td>
                  <td className={`px-3 ${cellPad} text-right`}>{costCell(l, "labor_cost", "Labor", `math-labor-${l.line_id}`)}</td>
                  <td className={`px-3 ${cellPad} text-right`}>{costCell(l, "equipment_cost", "Equipment", `math-equip-${l.line_id}`)}</td>
                  <td className={`px-3 ${cellPad} text-right`}>{costCell(l, "per_diem_cost", "Per-diem", `math-perdiem-${l.line_id}`)}</td>
                  <td className={`px-3 ${cellPad} text-right`}>{costCell(l, "material_cost", "Material", `math-material-${l.line_id}`)}</td>
                  <td className={`px-3 ${cellPad} text-right`}>{costCell(l, "subtotal", "Subtotal", `math-subtotal-${l.line_id}`)}</td>
                </tr>
              ); })())}
              {priced.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400">No scope to price yet.</td></tr>}
            </tbody>
          </table>
        </Card>

        {computed.warnings?.length > 0 && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="pricing-warnings">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1">
              <AlertCircle className="w-3.5 h-3.5" /> Not priced — missing inputs (never shown as zero)
            </div>
            <ul className="text-xs text-amber-700 space-y-0.5 list-disc pl-4">
              {computed.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        <ScopeGapCard bidId={bid.bid_id} />
        <SmartSuggestionsCard bid={bid} updateBid={updateBid} readOnly={readOnly} />
      </div>

      {/* Totals */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Roll-up</h2>
        {termsLocked && <div className="mb-3 rounded-md border border-[#003E87]/20 bg-[#003E87]/[0.06] px-3 py-2 text-[11px] text-[#003E87]" data-testid="rollup-terms-hint">Margins &amp; terms are set on the <b>Commercial Terms</b> tab — shown here read-only.</div>}
        {!bid.location_profile && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="no-location-warning">No project location set — weather productivity is assumed 100% and site tax isn't applied. Set it on the <b>Location</b> tab.</div>}
        {t && (
          <Card className="p-5 mb-4" data-testid="rollup-visuals">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Cost bridge — direct cost to price</div>
            <CostWaterfall steps={[
              { name: "Direct", amount: t.direct_cost, color: C.direct },
              { name: "Contingency", amount: t.contingency_amount, color: C.contingency },
              { name: "Profit", amount: t.markup_amount, color: C.profit },
              ...(t.bond_amount ? [{ name: "Bond", amount: t.bond_amount, color: C.bond }] : []),
              ...(t.warranty_amount ? [{ name: "Warranty", amount: t.warranty_amount, color: C.warranty }] : []),
            ]} />
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-4 mb-2">Where the direct cost sits</div>
            <CostDonut height={200}
              data={[
                ["labor", "Labor"], ["material", "Material"], ["equipment", "Equipment"], ["per_diem", "Per-diem"],
                ["subcontract", "Subcontract"], ["mobilization", "Mobilization"], ["general_conditions", "General Conditions"], ["management", "Management"],
              ].map(([k, lab]) => ({ label: lab, value: t[k] || 0, color: C[k] })).filter((d) => d.value > 0)}
              centerLabel="Direct" centerValue={money(t.direct_cost)} />
          </Card>
        )}
        <Card className="p-5 space-y-3">
          {t ? (
            <>
              <Row label="Labor" value={money(t.labor)} color={C.labor} />
              <Row label="Equipment" value={money(t.equipment)} color={C.equipment} />
              <Row label="Per-diem" value={money(t.per_diem)} color={C.per_diem} />
              {t.per_diem_crew ? <div className="pl-4 -mt-1 mb-1 text-[11px] text-slate-400 font-mono" data-testid="total-perdiem-split">crew {money(t.per_diem_crew)}{t.per_diem_line ? ` · line travel ${money(t.per_diem_line)}` : ""}</div> : null}
              <Row label="Material" value={money(t.material)} color={C.material} />
              {t.subcontract ? <Row label="Subcontract" value={money(t.subcontract)} color={C.subcontract} /> : null}
              {t.mobilization ? <Row label="Mobilization" value={money(t.mobilization)} color={C.mobilization} /> : null}
              {t.general_conditions ? <Row label="General Conditions" value={money(t.general_conditions)} color={C.general_conditions} /> : null}
              {t.management ? <Row label="Management" value={money(t.management)} color={C.management} /> : null}
              <div className="border-t border-slate-100 pt-3">
                <RowMath label="Direct cost" v={t.direct_cost} rows={[{ text: t.derivation.direct_cost, color: C.direct }]} summary={t.derivation.direct_cost} testid="total-direct" color={C.direct} />
              </div>

              <div className="pt-2 space-y-3">
                <MarginInput label="Contingency %" value={bid.contingency_pct} disabled={lockInputs}
                  onCommit={(v) => updateBid({ contingency_pct: v })} testid="contingency-input"
                  mode={bid.contingency_mode || "margin"} onMode={(mm) => updateBid({ contingency_mode: mm })} />
                <RowMath label="Contingency" v={t.contingency_amount} rows={[{ text: t.derivation.contingency, color: C.contingency }]} summary={t.derivation.contingency} testid="total-contingency" color={C.contingency} />
                <MarginInput label="Profit %" value={bid.margin_pct ?? bid.markup_pct} disabled={lockInputs}
                  onCommit={(v) => updateBid({ margin_pct: v })} testid="markup-input"
                  mode={bid.profit_mode || "margin"} onMode={(mm) => updateBid({ profit_mode: mm })} />
                <RowMath label="Profit" v={t.markup_amount} rows={[{ text: t.derivation.markup, color: C.profit }]} summary={t.derivation.markup} testid="total-markup" color={C.profit} />
                <MarginInput label="Bond %" value={bid.bond_pct} disabled={lockInputs}
                  onCommit={(v) => updateBid({ bond_pct: v })} testid="bond-input" />
                {t.bond_pct ? <RowMath label="Bond" v={t.bond_amount} rows={[{ text: t.derivation.bond, color: C.bond }]} summary={t.derivation.bond} testid="total-bond" color={C.bond} /> : null}
                <MarginInput label="Warranty %" value={bid.warranty_pct} disabled={lockInputs}
                  onCommit={(v) => updateBid({ warranty_pct: v })} testid="warranty-input" />
                {t.warranty_pct ? <RowMath label="Warranty" v={t.warranty_amount} rows={[{ text: `${t.warranty_pct}% × price = ${money(t.warranty_amount)}`, color: C.warranty }]} summary={`${t.warranty_pct}% × price`} testid="total-warranty" color={C.warranty} /> : null}
                <label className="flex items-center justify-between gap-2 py-1.5 mt-1 border-t border-slate-100 text-sm cursor-pointer" data-testid="equipment-passthru-toggle">
                  <span className="text-slate-600">Equipment is owner pass-through <span className="text-slate-400">(bill at cost, no markup)</span></span>
                  <input type="checkbox" checked={!!bid.equipment_pass_through} disabled={lockInputs} onChange={(e) => updateBid({ equipment_pass_through: e.target.checked })} data-testid="equipment-passthru-input" />
                </label>
                {t.equipment_pass_through && t.equipment_passthru_amount ? <RowMath label="Equipment (pass-through, at cost)" v={t.equipment_passthru_amount} rows={[{ text: `Owner pass-through — equipment added at cost after margin & bond: $${(t.equipment_passthru_amount).toLocaleString()}` }]} summary="at cost" testid="total-equip-passthru" /> : null}
                {t.global_tax_amount ? <RowMath label={`Sales/use tax (${t.global_tax_pct}%)`} v={t.global_tax_amount} rows={[{ text: t.derivation.global_tax }]} summary={t.tax_source || `${t.global_tax_pct}%`} testid="total-sales-tax" /> : null}
                {t.global_env_amount ? <RowMath label={`Environmental fee (${t.global_env_pct}%, rented equipment)`} v={t.global_env_amount} rows={[{ text: t.derivation.global_env }]} summary="rented equipment only" testid="total-env-fee" /> : null}
                <OtherFees bid={bid} totals={t} disabled={lockInputs} updateBid={updateBid} />
              </div>

              <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">Total</span>
                <ShowMath testid="total-price-math" label="Total contract price" value={money(t.total)}
                  tone="text-slate-900 text-xl font-bold"
                  rows={[
                    { title: "Direct", text: t.derivation.direct_cost },
                    { title: "Contingency", text: t.derivation.contingency },
                    { title: "Profit", text: t.derivation.markup },
                    ...(t.bond_pct ? [{ title: "Bond", text: t.derivation.bond }] : []),
                    ...(t.warranty_pct ? [{ title: "Warranty", text: `${t.warranty_pct}% × price = ${money(t.warranty_amount)}` }] : []),
                    ...((t.other_fees || []).map((f) => ({ title: f.name, text: `${f.pct}% of ${f.basis} = ${money(f.amount)}` }))),
                    ...(t.global_tax_amount ? [{ title: "Sales/use tax", text: t.derivation.global_tax }] : []),
                    ...(t.global_env_amount ? [{ title: "Environmental fee", text: t.derivation.global_env }] : []),
                    ...(t.standdown_days ? [{ title: "Stand-down", text: t.derivation.standdown }] : []),
                  ]}
                  summary={t.derivation.total} />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>$/Watt</span>
                <ShowMath testid="cost-per-watt-math" label="Cost per watt" value={t.cost_per_watt ? `$${t.cost_per_watt.toFixed(4)}/W` : "set MW"}
                  rows={[{ text: t.derivation.cost_per_watt }]} tone="text-slate-600" />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>On-site crew-days</span><span className="font-mono">{num(t.total_crew_days)}</span>
              </div>
              <div className="pt-3 mt-1 border-t border-slate-100" data-testid="cost-color-legend">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Color key — every calculation traces to these colors</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {COST_LEGEND.map(([k, lab]) => (
                    <span key={k} className="inline-flex items-center gap-1 text-[10px] text-slate-500" data-testid={`legend-${k}`}>
                      <span className="w-2 h-2 rounded-full" style={{ background: COST_COLORS[k] }} />{lab}
                    </span>
                  ))}
                </div>
              </div>
              <div className="pt-3 mt-1 border-t border-slate-100" data-testid="itemized-export">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Auditable itemized estimate — every department + roll-up</div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 w-20 shrink-0">By department</span>
                    <button onClick={() => dl("xlsx", "department")} data-testid="export-itemized-xlsx" className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Excel</button>
                    <button onClick={() => dl("pdf", "department")} data-testid="export-itemized-pdf" className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5"><Download className="w-3.5 h-3.5 text-rose-600" /> PDF</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 w-20 shrink-0">By cost code</span>
                    <button onClick={() => dl("xlsx", "cost_code")} data-testid="export-itemized-xlsx-cc" className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Excel</button>
                    <button onClick={() => dl("pdf", "cost_code")} data-testid="export-itemized-pdf-cc" className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5"><Download className="w-3.5 h-3.5 text-rose-600" /> PDF</button>
                  </div>
                </div>
              </div>
              {!perms.canSetMargin && <div className="text-[11px] text-slate-400 pt-1">Your role cannot set margin. Ask a chief estimator.</div>}
            </>
          ) : <div className="text-sm text-slate-400">Nothing priced yet.</div>}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-center text-sm">
      {color && <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ background: color }} />}
      <span className="text-slate-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-slate-300 mx-2 translate-y-1" />
      <span className="font-mono text-slate-700">{value}</span>
    </div>
  );
}
function RowMath({ label, v, rows, summary, testid, color }) {
  return (
    <div className="flex items-center text-sm">
      {color && <span className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ background: color }} />}
      <span className="text-slate-500">{label}</span>
      <span className="flex-1 border-b border-dotted border-slate-300 mx-2 translate-y-1" />
      <ShowMath testid={testid} label={label} value={money(v)} rows={rows} summary={summary} tone="text-slate-700 font-medium" accent={color} />
    </div>
  );
}
function MarginInput({ label, value, disabled, onCommit, testid, mode, onMode }) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => setV(value), [value]);
  return (
    <div className="flex items-center justify-between">
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="flex items-center gap-1.5">
        {onMode && (
          <select value={mode} disabled={disabled} onChange={(e) => onMode(e.target.value)} data-testid={`${testid}-mode`}
            className="h-8 text-[11px] border border-slate-200 rounded px-1 bg-white text-slate-600">
            <option value="margin">margin</option><option value="markup">markup</option>
          </select>
        )}
        <Input type="number" value={v ?? ""} disabled={disabled} data-testid={testid}
          onChange={(e) => setV(e.target.value === "" ? "" : Number(e.target.value))}
          onBlur={() => v !== value && onCommit(v)}
          className="h-8 w-20 text-right font-mono text-xs" />
      </div>
    </div>
  );
}

function OtherFees({ bid, totals, disabled, updateBid }) {
  const fees = bid.other_fees || [];
  const set = (next) => updateBid({ other_fees: next });
  const add = () => set([...fees, { name: "", pct: "", basis: "price" }]);
  const upd = (i, k, v) => set(fees.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const del = (i) => set(fees.filter((_, j) => j !== i));
  const amts = totals?.other_fees || [];
  return (
    <div className="pt-2 border-t border-slate-100" data-testid="other-fees">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600">Other fees &amp; %</span>
        {!disabled && <button onClick={add} data-testid="add-other-fee" className="text-[11px] text-[#003E87] hover:underline">+ Add fee</button>}
      </div>
      {fees.map((f, i) => (
        <div key={i} className="flex items-center gap-1.5 mb-1" data-testid={`other-fee-${i}`}>
          <input disabled={disabled} value={f.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Fee name" className="flex-1 min-w-0 text-[11px] border border-slate-200 rounded px-1.5 py-1 outline-none" />
          <input type="number" disabled={disabled} value={f.pct ?? ""} onChange={(e) => upd(i, "pct", e.target.value === "" ? "" : Number(e.target.value))} className="w-14 text-[11px] border border-slate-200 rounded px-1.5 py-1 text-right font-mono" placeholder="%" />
          <select disabled={disabled} value={f.basis || "price"} onChange={(e) => upd(i, "basis", e.target.value)} className="text-[11px] border border-slate-200 rounded px-1 py-1 bg-white"><option value="price">of price</option><option value="direct">of direct</option></select>
          {!disabled && <button onClick={() => del(i)} className="text-slate-300 hover:text-rose-600 text-xs shrink-0">✕</button>}
        </div>
      ))}
      {amts.map((f, i) => (
        <div key={`amt-${i}`} className="flex items-center justify-between text-[11px] text-slate-500"><span>{f.name}</span><span className="font-mono">{money(f.amount)}</span></div>
      ))}
    </div>
  );
}
